/**
 * Detección de plataforma y propagación de UTMs / fbclid a enlaces de tienda.
 * El script inline en <head> fija `data-os` antes del primer paint.
 */

import {
  initMetaPixel,
  trackMetaLead,
  trackMetaPageView,
  trackMetaViewContent,
} from "../../shared/meta-pixel";

const IOS_STORE =
  "https://apps.apple.com/co/app/elegibles/id6762829344";
const ANDROID_STORE =
  "https://play.google.com/store/apps/details?id=io.github.hernancho9.cnscapp";

type StoreOs = "android" | "ios";

/** Parámetros de campaña que se reenvían a la tienda (atribución parcial). */
function buildStoreAttributionQuery(): string {
  const params = new URLSearchParams(window.location.search);
  const parts: string[] = [];
  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

  for (const key of utmKeys) {
    const value = params.get(key)?.trim();
    if (value) parts.push(`${key}=${encodeURIComponent(value)}`);
  }

  const fbclid = params.get("fbclid")?.trim();
  if (fbclid) parts.push(`fbclid=${encodeURIComponent(fbclid)}`);

  return parts.join("&");
}

function withAndroidReferrer(baseUrl: string, attributionQuery: string): string {
  if (!attributionQuery) return baseUrl;
  const url = new URL(baseUrl);
  url.searchParams.set("referrer", attributionQuery);
  return url.toString();
}

function withIosAttribution(baseUrl: string, attributionQuery: string): string {
  if (!attributionQuery) return baseUrl;
  const url = new URL(baseUrl);
  // App Store ignora la mayoría de query params; se conservan por si Meta/ASC los usan.
  for (const pair of attributionQuery.split("&")) {
    const [rawKey, rawValue] = pair.split("=");
    if (!rawKey || rawValue === undefined) continue;
    const key = decodeURIComponent(rawKey);
    const value = decodeURIComponent(rawValue);
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
}

function storeUrlFor(os: StoreOs, attributionQuery: string): string {
  if (os === "android") {
    return withAndroidReferrer(ANDROID_STORE, attributionQuery);
  }
  return withIosAttribution(IOS_STORE, attributionQuery);
}

function applyStoreLinks(): void {
  const attributionQuery = buildStoreAttributionQuery();
  if (!attributionQuery) return;

  document.querySelectorAll<HTMLAnchorElement>("a[data-store]").forEach((anchor) => {
    const store = anchor.dataset.store as StoreOs | undefined;
    if (store !== "android" && store !== "ios") return;
    anchor.href = storeUrlFor(store, attributionQuery);
  });
}

function bindStoreLeadTracking(): void {
  document.querySelectorAll<HTMLAnchorElement>("a[data-store]").forEach((anchor) => {
    const store = anchor.dataset.store;
    if (store !== "android" && store !== "ios") return;
    anchor.addEventListener("click", () => {
      trackMetaLead({
        content_name: `app_store_${store}`,
        content_category: "app_download",
      });
    });
  });
}

applyStoreLinks();
bindStoreLeadTracking();

initMetaPixel();
trackMetaPageView();
trackMetaViewContent("links_landing");
